/**
 * Global API Manager with Rate Limiting and Request Queue
 * Prevents backend crashes by controlling API request flow
 */

import logger from './logger';

class ApiManager {
  constructor() {
    this.requestQueue = [];
    this.isProcessing = false;
    this.lastRequestTime = 0;
    this.MIN_REQUEST_INTERVAL = 2000; // 2 seconds between requests
    this.activeRequests = new Map(); // Track active requests by key
    this.requestCache = new Map(); // Cache responses
    this.CACHE_DURATION = 30000; // 30 seconds cache
  }

  /**
   * Add a request to the queue with cancellation support
   * @param {string} key - Unique key for this request
   * @param {Function} apiCall - Function that returns a Promise
   * @param {number} priority - Lower number = higher priority (default: 10)
   * @returns {Promise} - Resolves with API response
   */
  async enqueueRequest(key, apiCall, priority = 10) {

    // Cancel any existing request with the same key
    this.cancelRequest(key);

    // Check cache first
    const cached = this.getCachedResponse(key);
    if (cached) {
      return cached;
    }

    return new Promise((resolve, reject) => {
      const request = {
        key,
        apiCall,
        priority,
        resolve,
        reject,
        timestamp: Date.now(),
        cancelled: false
      };

      // Add to queue and sort by priority
      this.requestQueue.push(request);
      this.requestQueue.sort((a, b) => a.priority - b.priority);

      // Track active request
      this.activeRequests.set(key, request);

      // Start processing if not already running
      this.processQueue();
    });
  }

  /**
   * Cancel a specific request by key
   */
  cancelRequest(key) {
    // Mark as cancelled in active requests
    const activeRequest = this.activeRequests.get(key);
    if (activeRequest) {
      activeRequest.cancelled = true;
      this.activeRequests.delete(key);
    }

    // Remove from queue
    this.requestQueue = this.requestQueue.filter(req => {
      if (req.key === key) {
        req.cancelled = true;
        req.reject(new Error('Request cancelled'));
        return false;
      }
      return true;
    });
  }

  /**
   * Cancel ALL pending requests (use when switching wallets)
   */
  cancelAllRequests() {
    
    // Cancel all requests in queue
    this.requestQueue.forEach(req => {
      req.cancelled = true;
      req.reject(new Error('All requests cancelled'));
    });
    
    this.requestQueue = [];
    this.activeRequests.clear();
  }

  /**
   * Process the request queue with rate limiting
   */
  async processQueue() {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      // Enforce rate limiting
      const timeSinceLastRequest = Date.now() - this.lastRequestTime;
      if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
        const waitTime = this.MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        await this.sleep(waitTime);
      }

      // Get next request
      const request = this.requestQueue.shift();
      
      if (!request || request.cancelled) {
        continue;
      }

      try {
        this.lastRequestTime = Date.now();

        // Make the API call
        const response = await request.apiCall();
        
        // Cache the response
        this.cacheResponse(request.key, response);
        
        // Resolve if not cancelled
        if (!request.cancelled) {
          request.resolve(response);
        }
        
      } catch (error) {
        
        // Check for rate limiting error
        if (error.message && error.message.includes('Too many requests')) {
          await this.sleep(5000);
          
          // Try to use cached response if available
          const cached = this.getCachedResponse(request.key);
          if (cached && !request.cancelled) {
            request.resolve(cached);
          } else if (!request.cancelled) {
            request.reject(error);
          }
        } else if (!request.cancelled) {
          request.reject(error);
        }
      } finally {
        // Clean up
        this.activeRequests.delete(request.key);
      }
    }

    this.isProcessing = false;
  }

  /**
   * Cache API response
   */
  cacheResponse(key, response) {
    this.requestCache.set(key, {
      data: response,
      timestamp: Date.now()
    });
    
    // Clean old cache entries
    this.cleanCache();
  }

  /**
   * Get cached response if valid
   */
  getCachedResponse(key) {
    const cached = this.requestCache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  /**
   * Clean expired cache entries
   */
  cleanCache() {
    const now = Date.now();
    for (const [key, cached] of this.requestCache.entries()) {
      if (now - cached.timestamp > this.CACHE_DURATION) {
        this.requestCache.delete(key);
      }
    }
  }

  /**
   * Sleep utility
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get queue status for debugging
   */
  getStatus() {
    return {
      queueLength: this.requestQueue.length,
      isProcessing: this.isProcessing,
      activeRequests: this.activeRequests.size,
      cachedResponses: this.requestCache.size,
      lastRequestTime: this.lastRequestTime
    };
  }
}

// Create global singleton instance
export const apiManager = new ApiManager();

// Helper function for easy usage
export const makeApiRequest = (key, apiCall, priority = 10) => {
  return apiManager.enqueueRequest(key, apiCall, priority);
};

// Helper to cancel requests for a specific user/wallet
export const cancelUserRequests = (userAddress) => {
  if (userAddress) {
    // Cancel all requests related to this user
    ['deposits', 'referrals', 'trophies', 'transactions', 'registration'].forEach(type => {
      apiManager.cancelRequest(`${type}_${userAddress}`);
    });
  }
};

// Helper to cancel all requests (wallet switch)
export const cancelAllApiRequests = () => {
  apiManager.cancelAllRequests();
};

/**
 * API Manager with Retry Logic
 * Handles API calls with automatic retry on failure
 */

const DEFAULT_MAX_RETRIES = 1; // Reduced from 3 to 1 - only retry once
const DEFAULT_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 5000; // Reduced from 10s to 5s
const RETRY_MULTIPLIER = 2;

/**
 * Exponential backoff retry function
 * @param {Function} apiCall - Async function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} initialDelay - Initial delay in ms
 * @returns {Promise} Result of the API call
 */
export const retryApiCall = async (
  apiCall,
  maxRetries = DEFAULT_MAX_RETRIES,
  initialDelay = DEFAULT_RETRY_DELAY
) => {
  let lastError;
  let delay = initialDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      logger.debug(`[API-RETRY] Attempt ${attempt + 1}/${maxRetries + 1}`);
      const result = await apiCall();
      
      if (attempt > 0) {
        logger.info(`[API-RETRY] Success after ${attempt} retries`);
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable
      const isRetryable = isRetryableError(error);
      
      if (!isRetryable) {
        logger.warn(`[API-RETRY] Non-retryable error: ${error.message}`);
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        logger.warn(`[API-RETRY] Max retries (${maxRetries}) reached`);
        break;
      }

      // Calculate delay with exponential backoff
      const jitter = Math.random() * 1000; // Add random jitter 0-1s
      const nextDelay = Math.min(delay * RETRY_MULTIPLIER, MAX_RETRY_DELAY) + jitter;
      
      logger.debug(`[API-RETRY] Retrying in ${Math.round(nextDelay)}ms (attempt ${attempt + 1}/${maxRetries})`);
      
      await new Promise(resolve => setTimeout(resolve, nextDelay));
      delay = nextDelay;
    }
  }

  throw lastError;
};

/**
 * Check if an error is retryable
 * @param {Error} error - Error object
 * @returns {boolean} True if the error is retryable
 */
export const isRetryableError = (error) => {
  // Network errors are retryable
  if (!error.response) {
    return true;
  }

  const status = error.response.status;

  // Retry on server errors (5xx)
  if (status >= 500) {
    return true;
  }

  // Retry on timeout (408)
  if (status === 408) {
    return true;
  }

  // Retry on conflict (409) - might be transient
  if (status === 409 && error.response.data?.retryable === true) {
    return true;
  }

  // Retry on rate limit (429)
  if (status === 429) {
    return true;
  }

  // Don't retry client errors (4xx) except the ones above
  return false;
};

/**
 * Execute API call with automatic retry on network/server errors
 * @param {Function} apiCall - Async function to call
 * @param {string} description - Description for logging
 * @param {number} maxRetries - Maximum retries
 * @returns {Promise} Result of the API call
 */
export const executeWithRetry = async (
  apiCall,
  description = 'API Call',
  maxRetries = DEFAULT_MAX_RETRIES
) => {
  logger.debug(`[API-RETRY] Starting: ${description}`);
  
  try {
    const result = await retryApiCall(apiCall, maxRetries);
    logger.info(`[API-RETRY] Completed: ${description}`);
    return result;
  } catch (error) {
    logger.error(`[API-RETRY] Failed: ${description}`, error);
    throw error;
  }
};

const ApiRetryManager = {
  retryApiCall,
  executeWithRetry,
  isRetryableError
};

export default ApiRetryManager;
